/**
 * HomeView Component - PadelPulse Stitch Design
 * Displays overview of active leagues with modern UI/UX
 * Uses centralized layout system
 */

import { apiService } from "../services/api";
import type { League, DashboardStats } from "../types/index";
import { CreateLeagueModal } from "../components/CreateLeagueModal";
import { LeagueCard } from "../components/LeagueCard";
import { LeagueSettingsModal } from "../components/LeagueSettingsModal";
import { ConfirmationModal } from "../components/ConfirmationModal";
import { applyBaseLayout, setupHeaderListeners } from "../layouts/LayoutManager";
import { showNotification, buildShareUrl, copyToClipboard } from "../utils/ui.js";
import { t } from "../i18n/config";

export class HomeView {
  private container: HTMLElement;
  private createLeagueModal: CreateLeagueModal;
  private confirmationModal: ConfirmationModal;
  private settingsModal: LeagueSettingsModal;
  private currentFilter: "active" | "archived" = "active";

  constructor(container: HTMLElement) {
    this.container = container;
    this.createLeagueModal = new CreateLeagueModal();
    this.confirmationModal = new ConfirmationModal();
    this.settingsModal = new LeagueSettingsModal(() => this.loadLeagues());
  }

  async render(): Promise<void> {
    // Fetch dashboard stats and active leagues for the counters
    let stats: DashboardStats = { totalEnrollments: 0, distinctPlayers: 0, pendingMatches: 0 };
    let activeLeagues: League[] = [];

    try {
      const [statsData, leaguesData] = await Promise.all([
        apiService.getDashboardStats(),
        apiService.getLeagues("active")
      ]);
      stats = statsData;
      activeLeagues = leaguesData;
    } catch (error) {
      console.error("Failed to load dashboard data:", error);
    }

    const contentHTML = this.renderContent(stats, activeLeagues);
    const layoutHTML = applyBaseLayout({
      activePage: "home",
      container: this.container,
      content: contentHTML,
      title: "Your Leagues",
      description: "Manage and track all your paddle league tournaments in one place."
    });

    this.container.innerHTML = layoutHTML;
    await this.loadLeagues();
    this.setupEventListeners();
  }

  private renderContent(stats: DashboardStats, leagues: League[]): string {
    return `
      <!-- Page Header -->
      <div class="mb-12">
        <h1 class="text-4xl lg:text-5xl font-black text-slate-900 dark:text-white mb-3">${t("home.title")}</h1>
        <p class="text-slate-600 dark:text-slate-400 daylight:text-day-ink-soft text-lg max-w-2xl">${t("home.subtitle")}</p>
      </div>

      <!-- Stats Container -->
      <div class="mb-12">

        <!-- Stats Row -->
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
          
          <div class="stat-card bg-white dark:bg-slate-800 daylight:bg-day-card p-4 rounded-lg shadow daylight:shadow-day-sm border border-slate-200 dark:border-slate-700 daylight:border-day-ink/10">
            <div class="flex items-center gap-3 mb-2">
              <div class="p-2 bg-primary/10 rounded-lg text-primary">
                <span class="material-symbols-outlined">groups</span>
              </div>
              <h3 class="text-sm font-medium text-slate-500 dark:text-slate-400 daylight:text-day-ink-muted">${t("home.stats.totalPlayers")}</h3>
            </div>
            <p class="text-2xl font-bold text-slate-900 dark:text-white">${stats.totalEnrollments} <span class="text-sm text-slate-400 daylight:text-day-ink-muted font-normal">${t("home.stats.uniqueSuffix", { count: stats.distinctPlayers })}</span></p>
            <small class="text-xs text-slate-400 dark:text-slate-500 daylight:text-day-ink-muted">${t("home.stats.enrollmentsDistinct")}</small>
          </div>

          <div class="stat-card bg-white dark:bg-slate-800 daylight:bg-day-card p-4 rounded-lg shadow daylight:shadow-day-sm border border-slate-200 dark:border-slate-700 daylight:border-day-ink/10">
            <div class="flex items-center gap-3 mb-2">
              <div class="p-2 bg-warning/10 rounded-lg text-warning">
                <span class="material-symbols-outlined">sports_tennis</span>
              </div>
              <h3 class="text-sm font-medium text-slate-500 dark:text-slate-400 daylight:text-day-ink-muted">${t("home.stats.pendingMatches")}</h3>
            </div>
            <p class="text-2xl font-bold text-slate-900 dark:text-white">${stats.pendingMatches}</p>
            <small class="text-xs text-slate-400 dark:text-slate-500 daylight:text-day-ink-muted">${t("home.stats.pendingMatchesSub")}</small>
          </div>

          <div class="stat-card bg-white dark:bg-slate-800 daylight:bg-day-card p-4 rounded-lg shadow daylight:shadow-day-sm border border-slate-200 dark:border-slate-700 daylight:border-day-ink/10">
            <div class="flex items-center gap-3 mb-2">
              <div class="p-2 bg-success/10 rounded-lg text-success">
                <span class="material-symbols-outlined">trophy</span>
              </div>
              <h3 class="text-sm font-medium text-slate-500 dark:text-slate-400 daylight:text-day-ink-muted">${t("home.stats.activeLeagues")}</h3>
            </div>
            <p class="text-2xl font-bold text-slate-900 dark:text-white">${leagues.length}</p>
            <small class="text-xs text-slate-400 dark:text-slate-500 daylight:text-day-ink-muted">${t("home.stats.activeLeaguesSub")}</small>
          </div>
        </div>
      </div>

      <!-- Leagues Grid -->
      <div class="mb-8">
        <div class="flex items-center justify-between mb-6">
          <h2 class="text-2xl font-bold text-slate-900 dark:text-white" id="leagues-title">${t("home.leagues.activeTitle")}</h2>
          <div class="flex items-center gap-2 bg-slate-100 dark:bg-slate-800 daylight:bg-day-sunken rounded-lg p-1">
            <button id="filter-active" class="px-4 py-2 rounded-md text-sm font-medium transition-all filter-btn active bg-primary text-navy-800" data-status="active">
              ${t("home.filter.active")}
            </button>
            <button id="filter-archived" class="px-4 py-2 rounded-md text-sm font-medium transition-all filter-btn text-slate-600 dark:text-slate-400 daylight:text-day-ink-soft" data-status="archived">
              ${t("home.filter.archived")}
            </button>
          </div>
        </div>
        <div id="leagues-container" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <!-- League cards will be inserted here -->
        </div>
        
        <!-- Empty State -->
        <div id="empty-state" class="hidden">
          <div class="card text-center py-12">
            <span class="material-symbols-outlined text-6xl text-slate-300 dark:text-slate-700 flex justify-center mb-4">inbox</span>
            <h3 class="text-2xl font-bold text-slate-900 dark:text-white mb-2">${t("home.empty.title")}</h3>
            <p class="text-slate-600 dark:text-slate-400 daylight:text-day-ink-soft mb-6">${t("home.empty.body")}</p>
            <button id="empty-create-btn" class="btn-primary mx-auto">
              <span class="material-symbols-outlined">add</span>
              <span>${t("home.empty.createBtn")}</span>
            </button>
          </div>
        </div>
      </div>
    `;
  }

  private async loadLeagues(): Promise<void> {
    try {
      const leagues = await apiService.getLeagues(this.currentFilter);
      const container = this.container.querySelector("#leagues-container");
      const emptyState = this.container.querySelector("#empty-state");
      const leagueCount = this.container.querySelector("#league-count");
      const leaguesTitle = this.container.querySelector("#leagues-title");

      if (container) container.innerHTML = "";

      // Update title based on filter
      if (leaguesTitle) {
        leaguesTitle.textContent = this.currentFilter === "active"
          ? t("home.leagues.activeTitle")
          : t("home.leagues.archivedTitle");
      }

      if (leagues.length === 0) {
        emptyState?.classList.remove("hidden");
        if (leagueCount) leagueCount.textContent = "0";
        return;
      }

      emptyState?.classList.add("hidden");
      if (leagueCount) leagueCount.textContent = leagues.length.toString();

      leagues.forEach((league: League) => {
        const card = this.createLeagueCard(league);
        container?.appendChild(card);
      });
    } catch (error) {
      console.error("Failed to load leagues:", error);
    }
  }

  private createLeagueCard(league: League): HTMLElement {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = LeagueCard.render(league, true);
    const card = tempDiv.firstElementChild as HTMLElement;

    card.querySelector(".view-league-btn")?.addEventListener("click", () => {
      this.navigateToLeague(league.id);
    });

    card.querySelector(".copy-link-btn")?.addEventListener("click", (e) => {
      e.stopPropagation();
      if (league.sharingToken) this.copyShareLink(league.sharingToken);
    });

    card.querySelector(".settings-league-btn")?.addEventListener("click", (e) => {
      e.stopPropagation();
      this.settingsModal.show(league);
    });

    card.querySelector(".archive-league-btn")?.addEventListener("click", async (e) => {
      e.stopPropagation();
      await this.archiveLeague(league.id);
    });

    card.querySelector(".unarchive-league-btn")?.addEventListener("click", async (e) => {
      e.stopPropagation();
      await this.unarchiveLeague(league.id);
    });

    card.querySelector(".delete-league-btn")?.addEventListener("click", async (e) => {
      e.stopPropagation();
      await this.deleteLeague(league.id);
    });

    return card;
  }

  private navigateToLeague(leagueId: number): void {
    window.location.hash = `#league/${leagueId}`;
  }

  /**
   * Copy the public, no-login share link for a league to the clipboard. The URL
   * uses the unguessable sharing token (#/leagues/<token>) so it can be pasted
   * straight into WhatsApp. Falls back to a legacy copy path on older browsers.
   */
  private async copyShareLink(shareToken: string): Promise<void> {
    const ok = await copyToClipboard(buildShareUrl(shareToken));
    showNotification(
      ok ? t("home.share.copied") : t("home.share.failed"),
      ok ? "success" : "error"
    );
  }

  private setupEventListeners(): void {
    // Setup header listeners
    setupHeaderListeners(this.container, {
      onCreateLeague: () => this.showCreateLeagueModal(),
      onLogout: () => {
        localStorage.removeItem("authToken");
        window.location.hash = "#login";
      }
    });

    const emptyCreateBtn = this.container.querySelector("#empty-create-btn");
    const filterActive = this.container.querySelector("#filter-active");
    const filterArchived = this.container.querySelector("#filter-archived");

    emptyCreateBtn?.addEventListener("click", () => {
      this.showCreateLeagueModal();
    });

    filterActive?.addEventListener("click", () => {
      this.setFilter("active");
    });

    filterArchived?.addEventListener("click", () => {
      this.setFilter("archived");
    });
  }

  private setFilter(status: "active" | "archived"): void {
    this.currentFilter = status;

    // Update button styles
    const filterActive = this.container.querySelector("#filter-active");
    const filterArchived = this.container.querySelector("#filter-archived");

    // bg-primary is Pulse Lime in both skins, so the active pill must use
    // navy ink (text-navy-800) — the same contrast every other lime control
    // uses. text-white here is illegible on lime in the night skin.
    if (status === "active") {
      filterActive?.classList.add("bg-primary", "text-navy-800");
      filterActive?.classList.remove("text-slate-600", "dark:text-slate-400");
      filterArchived?.classList.remove("bg-primary", "text-navy-800");
      filterArchived?.classList.add("text-slate-600", "dark:text-slate-400");
    } else {
      filterArchived?.classList.add("bg-primary", "text-navy-800");
      filterArchived?.classList.remove("text-slate-600", "dark:text-slate-400");
      filterActive?.classList.remove("bg-primary", "text-navy-800");
      filterActive?.classList.add("text-slate-600", "dark:text-slate-400");
    }

    this.loadLeagues();
  }

  private async archiveLeague(leagueId: number): Promise<void> {
    const confirmed = await this.confirmationModal.open(
      t("home.archive.confirmTitle"),
      t("home.archive.confirmBody"),
      t("home.archive.confirmCta"),
      false
    );

    if (!confirmed) return;

    try {
      await apiService.archiveLeague(leagueId);
      showNotification(t("home.archive.success"), "success");
      this.loadLeagues();
    } catch (error) {
      console.error("Failed to archive league:", error);
      showNotification(t("home.archive.failed"), "error");
    }
  }

  private async unarchiveLeague(leagueId: number): Promise<void> {
    try {
      await apiService.unarchiveLeague(leagueId);
      showNotification(t("home.unarchive.success"), "success");
      this.loadLeagues();
    } catch (error) {
      console.error("Failed to unarchive league:", error);
      showNotification(t("home.unarchive.failed"), "error");
    }
  }

  private async deleteLeague(leagueId: number): Promise<void> {
    const confirmed = await this.confirmationModal.open(
      t("home.delete.confirmTitle"),
      t("home.delete.confirmBody"),
      t("home.delete.confirmCta"),
      true
    );

    if (!confirmed) return;

    try {
      await apiService.deleteLeague(leagueId);
      showNotification(t("home.delete.success"), "success");
      this.loadLeagues();
    } catch (error) {
      console.error("Failed to delete league:", error);
      showNotification(t("home.delete.failed"), "error");
    }
  }

  /**
   * Open the create-league modal from outside the view (e.g. when the router
   * lands on "#home/new" because the user clicked "New League" from another
   * page). Public wrapper around the private modal handler.
   */
  async promptCreateLeague(): Promise<void> {
    await this.showCreateLeagueModal();
  }

  private async showCreateLeagueModal(): Promise<void> {
    const result = await this.createLeagueModal.show();
    if (!result) return;

    try {
      await apiService.createLeague(result as any);
      this.loadLeagues();
    } catch (error) {
      console.error("Failed to create league:", error);
      showNotification(t("home.create.failed"), "error");
    }
  }
}